import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { FieldArray } from 'redux-form';
import get from 'lodash/get';
import { Grid, Field, Flex, Button, Segment, Divider } from 'nallian-shared-ui/lib/components';
import { SelectFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { ONBOARDING_TEMPLATE_FORM_NAME } from '../../../constants';
import ReadOnlyFieldValueComponent from '../../../../app/components/ReadOnlyFieldValueComponent';
import AppFeatureFields from './AppFeatureFields';

const FeatureGrid = styled(Grid)`
  width: calc(100% + 0.5rem);
`;

const FeatureSegment = styled(Segment)`
  width: 100%;
`;

const createAppOption = a => ({
  key: `${a.appInstallation?.id}_${a.appInstallation?.tag}`,
  value: `${a.appInstallation?.id}_${a.appInstallation?.tag}`,
  text: a.appInstallation?.name,
});

const App = ({
  index,
  fields,
  field,
  apps,
  submitProcessing,
  handleRemoveFieldClick,
  readOnly,
  editMode,
}) => {
  const { t } = useTranslation();
  // const dispatch = useDispatch();

  const isNew = get(fields.get(index), 'isNew');

  const { appInstallation } = useSelector(state =>
    get(state, `form.${ONBOARDING_TEMPLATE_FORM_NAME}.values.apps[${index}]`, {})
  );

  const splittedAppInstallation = useMemo(() => appInstallation?.split('_'), [appInstallation]);

  const appInstallationId = get(splittedAppInstallation, '[0]');

  const appOptions = useMemo(() => {
    const allFields = fields.getAll();

    return apps.map(createAppOption).filter(option => {
      const foundField = allFields.find(app => app.appInstallation === option.value);

      return (
        !foundField ||
        (foundField && index === allFields.findIndex(a => a.appInstallation === option.value))
      );
    });
  }, [apps, fields, index]);

  // useEffect(() => {
  // if (isNew) {
  // dispatch(change(ONBOARDING_TEMPLATE_FORM_NAME, `${field}.features`, []));
  // }
  // }, [appInstallation, isNew, dispatch, field]);

  return (
    <FeatureSegment secondary>
      <FeatureGrid>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            fluid
            placeholder={t('selectAnApp')}
            name={`${field}.appInstallation`}
            options={appOptions}
            valueComponent={
              readOnly || (editMode && !isNew)
                ? ReadOnlyFieldValueComponent
                : SelectFieldValueComponent
            }
            disabled={submitProcessing}
          />
        </Grid.Column>
        {appInstallationId && (
          <Grid.Column mobile={16} tablet={16} computer={16}>
            <FieldArray
              name={`${field}.features`}
              component={AppFeatureFields}
              editMode={editMode}
              readOnly={readOnly}
              apps={apps}
              appInstallationId={appInstallationId}
              submitProcessing={submitProcessing}
            />
          </Grid.Column>
        )}
      </FeatureGrid>
      {!readOnly && (
        <>
          <Divider />
          <Flex fluid direction="column" alignItems="flex-end">
            <Button
              linkbutton
              icon="close"
              type="button"
              tabindex="-1"
              content={t('removeApp')}
              i={index}
              onClick={handleRemoveFieldClick}
            />
          </Flex>
        </>
      )}
    </FeatureSegment>
  );
};

App.propTypes = {
  fields: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  field: PropTypes.string.isRequired,
  submitProcessing: PropTypes.bool.isRequired,
  apps: PropTypes.array.isRequired,
  handleRemoveFieldClick: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  editMode: PropTypes.bool.isRequired,
};

export default App;
